import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Typography, Button, Stack, IconButton, Tooltip, CircularProgress, Snackbar, Alert,
} from '@mui/material';
import FolderSharedRoundedIcon from '@mui/icons-material/FolderSharedRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { fmtSize } from '../utils/fileStore';
import { Panel, ConfirmDialog, SkeletonRows, LoadAlert } from './uiKit';
import { PRIMARY, PRIMARY_LIGHT } from '../theme';
import http, { apiErrorMessage } from '../Api/http';
import { UploadEmployeeDoc, GetEmployeeDocs, DownloadEmployeeDoc, DeleteEmployeeDoc } from '../Api/Api';
import { fetchFileBlob, saveBlob, blobErrorMessage } from '../utils/download';

export const EMP_DOC_TYPES = [
    { slug: 'profile-photo', label: 'Profile Photo' },
    { slug: 'aadhaar-card', label: 'Aadhaar Card' },
    { slug: 'pan-card', label: 'PAN Card' },
    { slug: 'passport', label: 'Passport' },
    { slug: 'resume', label: 'Resume' },
    { slug: 'educational-certificates', label: 'Educational Certificates' },
    { slug: 'experience-certificates', label: 'Experience Certificates' },
    { slug: 'offer-letter', label: 'Offer Letter' },
    { slug: 'appointment-letter', label: 'Appointment Letter' },
    { slug: 'relieving-letter', label: 'Relieving Letter' },
    { slug: 'bank-passbook', label: 'Bank Passbook / Cancelled Cheque' },
    { slug: 'other', label: 'Other' },
];
const REQUIRED_SLUGS = ['aadhaar-card', 'pan-card'];
const labelForSlug = (slug) => EMP_DOC_TYPES.find((t) => t.slug === slug)?.label || slug;

const normalizeDoc = (d) => ({
    id: d.id,
    documentType: d.documentType ?? '',
    fileName: d.fileName ?? '',
    url: d.url ?? '',
    contentType: d.contentType ?? '',
    sizeBytes: Number(d.sizeBytes) || 0,
    uploadedOn: d.uploadedOn ?? '',
});

// The same document slots the onboarding form offers (the profile photo lives in the
// employee's avatar, not in this list).
const DOC_SLOTS = EMP_DOC_TYPES.filter((t) => t.slug !== 'profile-photo');

export default function EmployeeDocuments({ employeeId }) {
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [uploadingSlug, setUploadingSlug] = useState(null);
    const [downloadingId, setDownloadingId] = useState(null);
    const [confirm, setConfirm] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [snack, setSnack] = useState(null);
    const fileInput = useRef(null);
    const pendingSlug = useRef(null);

    const notify = (msg, sev = 'success') => setSnack({ msg, sev });

    const load = useCallback(async () => {
        if (employeeId == null) { setLoading(false); return; }
        setLoading(true);
        try {
            const { data: body } = await http.get(GetEmployeeDocs, { params: { employeeId } });
            // "no documents" comes back as a 404 / error flag; every slot then simply reads "No file uploaded".
            const noDocs = body?.error && /no document|not found|no record|no data/i.test(body.message || '');
            if (body?.error && !noDocs) throw new Error(body.message || 'Could not load documents.');
            const list = Array.isArray(body?.data?.documents) ? body.data.documents : [];
            setDocs(list.map(normalizeDoc));
            setLoadError('');
        } catch (err) {
            if (err?.response?.status === 404) { setDocs([]); setLoadError(''); }
            else setLoadError(err?.response || err?.request ? apiErrorMessage(err, 'Could not load documents.') : (err.message || 'Could not load documents.'));
        } finally {
            setLoading(false);
        }
    }, [employeeId]);

    useEffect(() => { load(); }, [load]);

    const docFor = (slug) => docs.find((d) => d.documentType === slug) || null;
    const slugSet = new Set(DOC_SLOTS.map((t) => t.slug));
    const extras = docs.filter((d) => !slugSet.has(d.documentType));
    const uploadedCount = DOC_SLOTS.filter((t) => docFor(t.slug)).length;

    const chooseFor = (slug) => {
        if (uploadingSlug || employeeId == null) return;
        pendingSlug.current = slug;
        fileInput.current?.click();
    };

    const onPickFile = async (e) => {
        const file = e.target.files?.[0];
        const slug = pendingSlug.current;
        e.target.value = '';
        if (!file || !slug || uploadingSlug) return;
        setUploadingSlug(slug);
        try {
            const fd = new FormData();
            fd.append('employeeId', String(employeeId));
            fd.append('documentType', slug);
            fd.append('file', file);
            const { data: body } = await http.post(UploadEmployeeDoc, fd);
            if (body?.error) throw new Error(body.message || 'Could not upload the document.');
            notify(body?.message || `${labelForSlug(slug)} uploaded.`);
            await load();
        } catch (err) {
            notify(err?.response || err?.request ? apiErrorMessage(err, 'Could not upload the document.') : (err.message || 'Could not upload the document.'), 'error');
        } finally {
            setUploadingSlug(null);
            pendingSlug.current = null;
        }
    };

    const view = (d) => {
        if (d.url) window.open(d.url, '_blank', 'noopener');
        else notify('This document has no viewable file.', 'warning');
    };

    const download = async (d) => {
        if (downloadingId) return;
        setDownloadingId(d.id);
        try {
            const { blob, name } = await fetchFileBlob(DownloadEmployeeDoc, { id: d.id });
            saveBlob(blob, name || d.fileName);
        } catch (err) {
            notify(await blobErrorMessage(err, 'Could not download the file.'), 'error');
        } finally {
            setDownloadingId(null);
        }
    };

    const doDelete = async () => {
        if (!confirm || deleting) return;
        setDeleting(true);
        try {
            const { data: body } = await http.delete(DeleteEmployeeDoc, { params: { id: confirm.id } });
            if (body?.error) throw new Error(body.message || 'Could not remove the document.');
            notify(body?.message || `${labelForSlug(confirm.documentType)} removed.`);
            setConfirm(null);
            await load();
        } catch (err) {
            notify(err?.response || err?.request ? apiErrorMessage(err, 'Could not remove the document.') : (err.message || 'Could not remove the document.'), 'error');
        } finally {
            setDeleting(false);
        }
    };

    const iconBtn = { color: '#64748B', '&:hover': { color: PRIMARY, bgcolor: PRIMARY_LIGHT } };

    // One tile per document type: the file on record, or a clear "No file uploaded".
    const tile = (key, label, d, slug) => {
        const busyUpload = uploadingSlug === slug;
        const busyDownload = d && downloadingId === d.id;
        return (
            <Stack
                key={key} direction="row"
                sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1.5, px: 1.8, py: 1.3, minHeight: 66, border: '1px solid #EEF0F6', borderRadius: '9px', bgcolor: d ? '#fff' : '#FAFAFD' }}
            >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#1F2937' }}>{label}{REQUIRED_SLUGS.includes(slug) && <span style={{ color: '#E11D48', fontSize: 11, marginLeft: 6 }}>Required</span>}</Typography>
                    {d ? (
                        <>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#334155', mt: 0.2 }} noWrap>{d.fileName}</Typography>
                            <Typography sx={{ fontSize: 11, color: '#98A0AE' }}>{fmtSize(d.sizeBytes)}{d.uploadedOn ? ` · ${d.uploadedOn}` : ''}</Typography>
                        </>
                    ) : (
                        <Typography sx={{ fontSize: 12, color: '#A8B0BE', mt: 0.2 }}>No file uploaded</Typography>
                    )}
                </Box>
                {d ? (
                    <Stack direction="row" spacing={0.3} sx={{ flexShrink: 0 }}>
                        <Tooltip arrow title="View"><IconButton size="small" onClick={() => view(d)} sx={iconBtn}><VisibilityRoundedIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                        <Tooltip arrow title="Download">
                            <span><IconButton size="small" onClick={() => download(d)} disabled={Boolean(busyDownload)} sx={iconBtn}>
                                {busyDownload ? <CircularProgress size={16} /> : <DownloadRoundedIcon sx={{ fontSize: 18 }} />}
                            </IconButton></span>
                        </Tooltip>
                        {slug && (
                            <Tooltip arrow title="Replace">
                                <span><IconButton size="small" onClick={() => chooseFor(slug)} disabled={busyUpload || Boolean(uploadingSlug)} sx={iconBtn}>
                                    {busyUpload ? <CircularProgress size={16} /> : <UploadFileRoundedIcon sx={{ fontSize: 18 }} />}
                                </IconButton></span>
                            </Tooltip>
                        )}
                        {!REQUIRED_SLUGS.includes(slug) && <Tooltip arrow title="Remove"><IconButton size="small" onClick={() => setConfirm(d)} sx={{ color: '#94A3B8', '&:hover': { color: '#E11D48', bgcolor: '#FEF2F2' } }}><DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>}
                    </Stack>
                ) : (
                    <Button
                        onClick={() => chooseFor(slug)} disabled={Boolean(uploadingSlug) || employeeId == null}
                        startIcon={busyUpload ? <CircularProgress size={14} /> : <UploadFileRoundedIcon sx={{ fontSize: 17 }} />}
                        sx={{ flexShrink: 0, bgcolor: PRIMARY_LIGHT, color: PRIMARY, border: '1px solid #C9BEFB', fontWeight: 700, borderRadius: '7px', textTransform: 'none', height: 34, px: 1.6, fontSize: 12.5, boxShadow: 'none', '&:hover': { bgcolor: '#E7DFFC' } }}
                    >
                        {busyUpload ? 'Uploading…' : 'Choose'}
                    </Button>
                )}
            </Stack>
        );
    };

    return (
        <Panel
            title="Documents"
            icon={FolderSharedRoundedIcon}
            chip={loading ? 'Loading…' : `${uploadedCount} of ${DOC_SLOTS.length} uploaded`}
            action={(
                <Tooltip arrow title="Reload">
                    <IconButton onClick={load} disabled={loading} sx={{ border: '1px solid #E6EAF1', borderRadius: '7px', color: '#64748B', height: 38, width: 38, '&:hover': { bgcolor: PRIMARY_LIGHT, color: PRIMARY } }}>
                        <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
            )}
        >
            <input ref={fileInput} type="file" hidden onChange={onPickFile} />
            {loading ? (
                <SkeletonRows count={5} />
            ) : loadError ? (
                <Box sx={{ p: 2.5 }}>
                    <LoadAlert sx={{ borderRadius: '9px' }}>
                        {loadError}
                    </LoadAlert>
                </Box>
            ) : (
                <Box sx={{ p: 1.8 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 1.4 }}>
                        {DOC_SLOTS.map((t) => tile(t.slug, t.label, docFor(t.slug), t.slug))}
                        {extras.map((d) => tile(`extra-${d.id}`, labelForSlug(d.documentType), d, null))}
                    </Box>
                </Box>
            )}

            <ConfirmDialog
                open={Boolean(confirm)}
                onClose={() => { if (!deleting) setConfirm(null); }}
                onConfirm={doDelete}
                title="Remove this document?"
                body={confirm ? `${labelForSlug(confirm.documentType)} (${confirm.fileName}) will be permanently removed from this employee.` : ''}
                confirmLabel="Remove"
            />

            <Snackbar open={Boolean(snack)} autoHideDuration={3400} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={() => setSnack(null)} severity={snack?.sev || 'success'} variant="filled" sx={{ borderRadius: '7px' }}>{snack?.msg}</Alert>
            </Snackbar>
        </Panel>
    );
}
